import { Box, ButtonBase } from '@mui/material';
import { useId } from 'react';

type PillStyle = { bg: string; fg: string; border: string };

export type PillOption<T extends string> = { value: T; label: string; style: PillStyle };

export function PillRadioGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  required = false,
  missingHint,
  showLabel = true,
  disabled = false,
}: {
  label: string;
  options: PillOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  required?: boolean;
  /** Shown under the pills while a required pick is still missing. */
  missingHint?: string | null;
  showLabel?: boolean;
  disabled?: boolean;
}) {
  const headingId = useId();
  return (
    <Box>
      {showLabel ? (
        <Box id={headingId} sx={{ fontSize: 12, color: 'text.secondary', mb: '6px' }}>
          {label}
          {required ? (
            <Box component="span" sx={{ color: 'text.disabled' }}>
              {' '}
              (required)
            </Box>
          ) : null}
        </Box>
      ) : null}
      <Box
        role="radiogroup"
        {...(showLabel ? { 'aria-labelledby': headingId } : { 'aria-label': label })}
        aria-required={required || undefined}
        sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}
      >
        {options.map((option) => {
          const checked = option.value === value;
          const { style } = option;
          return (
            <ButtonBase
              key={option.value}
              role="radio"
              aria-checked={checked}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              sx={{
                px: '10px',
                py: '4px',
                borderRadius: '999px',
                fontSize: 13,
                fontWeight: checked ? 600 : 500,
                border: '1px solid',
                borderColor: checked ? style.fg : style.border,
                bgcolor: checked ? style.bg : '#fff',
                color: style.fg,
                boxShadow: checked ? `0 0 0 1px ${style.fg}` : 'none',
                '&.Mui-disabled': { opacity: 0.6 },
              }}
            >
              {option.label}
            </ButtonBase>
          );
        })}
      </Box>
      {missingHint ? (
        <Box sx={{ fontSize: 12, color: 'error.main', mt: '6px' }}>{missingHint}</Box>
      ) : null}
    </Box>
  );
}
